import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

export default function ChangePassword() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Ny adgangskode skal være mindst 8 tegn');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Adgangskoderne stemmer ikke overens');
      return;
    }

    if (currentPassword === newPassword) {
      setError('Ny adgangskode skal være forskellig fra den nuværende');
      return;
    }

    setIsLoading(true);

    try {
      await api.changePassword(currentPassword, newPassword);
      await refreshUser();
      navigate('/');
    } catch {
      setError('Forkert nuværende adgangskode');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockResetIcon color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
              Skift adgangskode
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Du skal vælge en ny adgangskode for at fortsætte
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nuværende adgangskode"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="dense"
              required
              autoFocus
              autoComplete="current-password"
            />
            <TextField
              fullWidth
              label="Ny adgangskode"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="dense"
              required
              autoComplete="new-password"
              helperText="Mindst 8 tegn"
            />
            <TextField
              fullWidth
              label="Bekræft ny adgangskode"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="dense"
              required
              autoComplete="new-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Skift adgangskode'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
